import type { Database } from "@-/com.npmjs.tursodatabase__sync-react-native";
import onetime from "@-/com.npmjs.onetime";
import {
  isDeadlineExceededError,
  withDeadline,
  type DeadlineExceededError,
} from "@-/dev.improve.haniverse.lib.deadline";
import { nativeDatabaseGenerations, sealDatabaseGeneration } from "#native-database-generations";
import { resolveSqlitePath } from "#resolve-sqlite-path";
import { DatabaseRecoveryRequiredError, isDatabaseCorruption } from "#database-corruption-error";
import type { DatabaseGenerations } from "#database-generations";
import { DRAINS_IN_FLIGHT_ON_CLOSE } from "#turso-adapter";

/**
 * Per-call deadlines for native engine calls. The owned engine below is the ONE chokepoint every
 * native call passes through, so this is where "a native call that never returns" becomes an
 * error instead of an endless await. Each value is a "certainly wedged" threshold for its call:
 *
 *   - `connect` of an EXISTING replica is local (bootstrap is disabled for published generations)
 *     and finishes in milliseconds; 20s only catches a wedge. A FRESH generation's connect is the
 *     cloud bootstrap — the whole account downloaded — so it gets 60s; the engine exposes no
 *     progress to restart a deadline on, so it is a generous fixed bound. There is deliberately NO
 *     retry ladder natively: a retry would have to reset files, which is forbidden for an
 *     established replica, and a fresh candidate that failed is never reused anyway.
 *   - `waitChanges` 90s: the pull's HTTP round-trip, with long polling disabled. Each request is
 *     bounded at 45s, body included (`actor-sync-fetch`), so a foreground network hang always
 *     surfaces as an ordinary error well inside this. The pull's CALLER gives up at 30s
 *     (`lock-turso-sync-phases`). What still reaches this deadline is an app suspended mid-request:
 *     JavaScript timers do not run while iOS suspends the app, so on resume every expired deadline
 *     fires at once — this one included — and the sync half is marked stalled until the call
 *     settles (see `syncStalled` below).
 *   - `push` 180s: an upload of every unsent change on a slow link, no progress signal exposed.
 *   - `applyChanges` 120s: applying a large pulled batch on a slow device.
 *   - `PRAGMA integrity_check` 60s: proportional to database size.
 *   - everything else (a SQL statement, a PRAGMA) 30s.
 */
const CONNECT_EXISTING_DEADLINE_MS = 20_000;
const CONNECT_BOOTSTRAP_DEADLINE_MS = 60_000;
const INTEGRITY_CHECK_DEADLINE_MS = 60_000;
const DEFAULT_CALL_DEADLINE_MS = 30_000;
const CALL_DEADLINES_MS: Readonly<Record<string, number>> = {
  applyChanges: 120_000,
  push: 180_000,
  waitChanges: 90_000,
};

/**
 * Calls that run on the engine's SYNC machinery only — the HTTP round-trips, the engine-owned
 * `-changes` file and sync metadata, and push's separate CDC reader connections — never on the
 * main connection. One of these stalling makes cloud sync unusable on this connection, but local
 * studying on the main connection stays safe, so it does not take the whole connection down.
 * `applyChanges` is NOT here: it opens the engine's write transaction on the main connection.
 */
const SYNC_ONLY_CALLS: ReadonlySet<string> = new Set(["pull", "push", "stats", "waitChanges"]);

/**
 * How long a close's CALLER waits for in-flight engine calls to settle and the native close to
 * confirm. The close itself is not cancelled when this passes — it keeps waiting in the background
 * and releases ownership once it confirms (see `close` below) — so this only decides how long the
 * app's lifecycle waits before it abandons the handle and reports the failure.
 */
const CLOSE_AWAIT_DEADLINE_MS = 15_000;

/**
 * How long a new open waits for a previous, abandoned close of the same database to finish before
 * claiming. Usually the straggler that made the close overrun settles soon after; waiting a moment
 * turns "Try again" into a success instead of an immediate lease refusal.
 */
const ABANDONED_CLOSE_GRACE_MS = 5000;

/**
 * An engine call never answered within its deadline. The call may still be running inside the
 * native engine. For a main-connection call this connection is never used again: every later call
 * fails with this error at once, and the owner (`contexts/database/actor`'s lifecycle, via
 * `onDamaged`) replaces it. For a sync-only call only the sync half is refused, and only until
 * that call settles. The sync supervisor classifies it as an engine fault, so a stall that never
 * settles ends in a reopen rather than an endless "cloud unreachable".
 */
export class DatabaseEngineStalledError extends Error {
  readonly call: string;

  constructor(databaseName: string, call: string, cause: DeadlineExceededError) {
    super(
      `${databaseName}: the database engine's ${call} timed out after ${cause.deadlineMs}ms and never answered, so this connection was abandoned`,
      { cause },
    );
    this.name = "DatabaseEngineStalledError";
    this.call = call;
  }
}

/**
 * The previous connection to this database was abandoned while an engine call was still running
 * inside it, and it has not finished closing. Its ownership lease is deliberately still held — a
 * second engine on the same files is the corruption this lifecycle exists to prevent — so the open
 * is refused. It reopens by itself once the straggler settles; a restart ends it for certain.
 */
export class DatabaseStillClosingError extends Error {
  constructor(databaseName: string, cause: unknown) {
    super(
      `${databaseName} is still closing after a database engine call timed out. Try again in a moment, or restart Han.`,
      { cause },
    );
    this.name = "DatabaseStillClosingError";
  }
}

// Diagnostic + grace registry of closes that outlived their awaiters, keyed by logical database.
// Runtime-lifetime by nature: it describes native handles this JavaScript runtime still owns.
const getAbandonedCloses = onetime(() => new Map<string, Promise<void>>());

function trackAbandonedClose(databaseName: string, reclaim: Promise<void>): void {
  const abandoned = getAbandonedCloses();
  abandoned.set(databaseName, reclaim);
  void reclaim.then(
    () => {
      if (abandoned.get(databaseName) === reclaim) {
        abandoned.delete(databaseName);
      }
    },
    () => {
      // A failed native close keeps ownership: the entry stays so the next open says why.
    },
  );
}

const getNativeEnvironment = onetime(() => ({
  generations: nativeDatabaseGenerations,
  resolvePath: resolveSqlitePath,
  seal: sealDatabaseGeneration,
}));

/** No caller receives a candidate before validation and durable publication. */
export async function openOwnedTursoGeneration(
  databaseName: string,
  create: (path: string, fresh: boolean) => Promise<Database>,
  environment: {
    generations: DatabaseGenerations;
    resolvePath: (path: string) => string;
    seal: (path: string) => void;
  } = getNativeEnvironment(),
  options: {
    /**
     * Called once when an engine call on the MAIN connection stalls past its deadline: the
     * connection is abandoned and every later call fails fast, so the owner should replace it.
     */
    onDamaged?: (error: unknown) => void;
  } = {},
) {
  const previousClose = getAbandonedCloses().get(databaseName);
  if (previousClose !== undefined) {
    await withDeadline(
      {
        deadline: ABANDONED_CLOSE_GRACE_MS,
        operation: `Opening ${databaseName} (waiting for the previous connection to finish closing)`,
      },
      () => previousClose,
    ).catch(() => {
      // Still closing (or its close failed): the claim below refuses, and says why.
    });
  }
  let lease: ReturnType<DatabaseGenerations["claim"]>;
  try {
    lease = environment.generations.claim(databaseName);
  } catch (error) {
    if (getAbandonedCloses().has(databaseName)) {
      throw new DatabaseStillClosingError(databaseName, error);
    }
    throw error;
  }
  const databasePath = environment.resolvePath(lease.path);
  let engine: Database | undefined;
  // Engines whose native close has already been issued, so an abandoned-open reclaim never closes
  // one twice.
  const closeIssued = new WeakSet<Database>();
  // Native calls made during the open that have not settled. A call that outlived its deadline is
  // still running inside `engine`, so the failure path must not close the engine under it.
  const openCalls = new Set<Promise<unknown>>();
  const callDuringOpen = <T>(
    call: string,
    deadlineMs: number,
    run: () => Promise<T> | T,
  ): Promise<T> => {
    const native = Promise.resolve().then(run);
    openCalls.add(native);
    const forget = () => openCalls.delete(native);
    void native.then(forget, forget);
    return withDeadline(
      { deadline: deadlineMs, operation: `Opening ${databaseName}: ${call}` },
      () => native,
    );
  };
  try {
    const connect = async (target: Database, bootstrap: boolean): Promise<void> => {
      await callDuringOpen(
        "connect",
        bootstrap ? CONNECT_BOOTSTRAP_DEADLINE_MS : CONNECT_EXISTING_DEADLINE_MS,
        () => target.connect(),
      );
      await callDuringOpen("PRAGMA synchronous", DEFAULT_CALL_DEADLINE_MS, () =>
        target.exec("PRAGMA synchronous = FULL"),
      );
    };
    engine = await create(databasePath, lease.fresh);
    const candidate = engine;
    await connect(candidate, lease.fresh);
    const integrity = await callDuringOpen(
      "PRAGMA integrity_check",
      INTEGRITY_CHECK_DEADLINE_MS,
      () => candidate.all("PRAGMA integrity_check"),
    );
    if (integrity.length !== 1 || integrity[0]?.integrity_check !== "ok") {
      lease.quarantine();
      throw new DatabaseRecoveryRequiredError();
    }
    if (lease.fresh) {
      // Seal only CLOSED files. Raw same-process file reads/closes on a live
      // SQLite file can drop that engine's POSIX locks.
      closeIssued.add(candidate);
      await callDuringOpen("close", DEFAULT_CALL_DEADLINE_MS, () => candidate.close());
      engine = undefined;
      environment.seal(lease.path);
      lease.publish();
      engine = await create(databasePath, false);
      await connect(engine, false);
    } else {
      lease.publish();
    }
    const opened = engine;
    // The RAW native calls still running. Close waits for these — not for the bounded wrappers
    // handed to callers, which settle at their deadline while the native call may run on.
    const pending = new Set<Promise<unknown>>();
    let closing: Promise<void> | undefined;
    let quarantined = false;
    // Set when a call outlived its deadline: the engine (or its sync half) may still be running it.
    let stalled: DatabaseEngineStalledError | undefined;
    let syncStalled: DatabaseEngineStalledError | undefined;
    const close = (): Promise<void> => {
      closing ??= (async () => {
        // Includes network waits that still write engine-owned sidecars, and any call that
        // outlived its deadline. Never release ownership while one may still be running: this
        // wait has no deadline because it is the ownership itself, not an awaiter — callers are
        // bounded below, and a runtime reload ends it along with the engine.
        await Promise.allSettled(pending);
        await opened.close();
        lease.release();
      })();
      const attempt = closing;
      return withDeadline(
        {
          deadline: CLOSE_AWAIT_DEADLINE_MS,
          operation: `Closing ${databaseName} (waiting for in-flight engine calls, then the native close)`,
        },
        () => attempt,
      ).catch((error: unknown) => {
        if (isDeadlineExceededError(error)) {
          // Abandoned, not released: the attempt keeps running and releases ownership once the
          // stragglers settle and the native close confirms. A new open waits briefly for it.
          trackAbandonedClose(databaseName, attempt);
        }
        throw error;
      });
    };
    const owned = new Proxy(opened, {
      get(target, property) {
        if (property === "close") {
          return close;
        }
        if (property === DRAINS_IN_FLIGHT_ON_CLOSE) {
          // Tells the adapter it may close this engine even after its lock was poisoned: this
          // close waits for every raw native call itself, so it can never interleave with one.
          return true;
        }
        const value: unknown = Reflect.get(target, property, target);
        if (typeof value !== "function") {
          return value;
        }
        const call = String(property);
        return (...args: unknown[]) => {
          if (closing !== undefined) {
            return Promise.reject(new Error("Database connection is closing"));
          }
          if (quarantined) {
            return Promise.reject(new DatabaseRecoveryRequiredError());
          }
          if (stalled !== undefined) {
            return Promise.reject(stalled);
          }
          if (syncStalled !== undefined && SYNC_ONLY_CALLS.has(call)) {
            return Promise.reject(syncStalled);
          }
          const native = Promise.resolve().then(() => Reflect.apply(value, target, args));
          pending.add(native);
          const forget = () => pending.delete(native);
          void native.then(forget, forget);
          const deadlineMs = CALL_DEADLINES_MS[call] ?? DEFAULT_CALL_DEADLINE_MS;
          const operation = `${databaseName} ${call}`;
          return withDeadline({ deadline: deadlineMs, operation }, () => native).catch(
            (error: unknown) => {
              if (isDeadlineExceededError(error) && error.operation === operation) {
                const stall = new DatabaseEngineStalledError(databaseName, call, error);
                if (SYNC_ONLY_CALLS.has(call)) {
                  if (syncStalled === undefined) {
                    syncStalled = stall;
                    // Nothing else may enter the sync machinery while the stalled call is still
                    // inside it. Once it settles — a request in flight while iOS suspended the app
                    // is aborted moments after resume — sync is safe again. Latching forever left
                    // sync dead for the rest of the session behind a "will resume by itself".
                    const heal = () => {
                      if (syncStalled === stall) {
                        syncStalled = undefined;
                      }
                    };
                    void native.then(heal, heal);
                  }
                } else if (stalled === undefined) {
                  stalled = stall;
                  try {
                    options.onDamaged?.(stall);
                  } catch {
                    // The owner's reaction cannot change that this connection is unusable.
                  }
                }
                throw stall;
              }
              if (isDatabaseCorruption(error)) {
                quarantined = true;
                try {
                  lease.quarantine();
                } catch (quarantineError) {
                  // oxlint-disable-next-line eslint/preserve-caught-error -- AggregateError.errors retains quarantineError; cause preserves the database corruption failure.
                  throw new AggregateError(
                    [error, quarantineError],
                    "Database corruption and quarantine persistence failed",
                    { cause: error },
                  );
                }
                throw new DatabaseRecoveryRequiredError(error);
              }
              throw error;
            },
          );
        };
      },
    });
    return { databasePath, hasLocalReplica: !lease.fresh, tursoDb: owned };
  } catch (error) {
    if (isDatabaseCorruption(error)) {
      // Keep the original exception even if persisting quarantine also fails.
      try {
        lease.quarantine();
      } catch {
        /* Never delete or reopen to recover. */
      }
    }
    if (engine !== undefined && openCalls.size > 0) {
      // An open call outlived its deadline and is still inside the engine. Closing it now would
      // interleave with that call, and releasing the lease would let the next open put a second
      // engine on these files. Abandon it instead: close and release only once it settles.
      const stranded = engine;
      const straggling = [...openCalls];
      const reclaim = (async () => {
        await Promise.allSettled(straggling);
        if (!closeIssued.has(stranded)) {
          await stranded.close();
        }
        lease.release();
      })();
      trackAbandonedClose(databaseName, reclaim);
      throw error;
    }
    // An unsuccessful close retains the lease. Reopening or resetting underneath
    // an unconfirmed live native handle is forbidden, even during error recovery.
    if (engine !== undefined) {
      const failedEngine = engine;
      const nativeClose = Promise.resolve().then(() => failedEngine.close());
      try {
        await withDeadline(
          {
            deadline: DEFAULT_CALL_DEADLINE_MS,
            operation: `Closing ${databaseName} after a failed open`,
          },
          () => nativeClose,
        );
      } catch (closeError) {
        if (isDeadlineExceededError(closeError)) {
          // Unconfirmed: ownership stays with the handle until its close confirms.
          trackAbandonedClose(
            databaseName,
            nativeClose.then(() => {
              lease.release();
            }),
          );
        }
        // oxlint-disable-next-line eslint/preserve-caught-error -- AggregateError.errors retains closeError; cause preserves the original open failure.
        throw new AggregateError(
          [error, closeError],
          "Database open and close failed; ownership retained",
          { cause: error },
        );
      }
    }
    lease.release();
    throw error;
  }
}
